import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { InputProptypes, wrap } from 'components/form/InputWrapper'
import {
  getClipboardData,
  isNullOrUndefined,
  isPresent,
  isValidUrl,
  noop,
  pasteAsText,
} from 'utils'
import { useUpdatingRef } from 'utils/hooks/useUpdatingRef'

const addStyles = (text, disabled) => {
  if (!isPresent(text)) return ''
  if (disabled) return `<span style='color:#ccc'>${text}</span>`
  if (!isValidUrl(text)) {
    throw new TypeError(`${text} is not a valid URL`)
  }
  return text.replace(
    /^((https?):\/\/([^/:\s]+)(:\d+)?)/gi,
    '<span class="fc_emph">$1</span>'
  )
}

const URLInput = forwardRef(
  (
    {
      id,
      uniqIndex,
      name,
      value,
      onFocus,
      onBlur,
      onChange,
      maxLength,
      disabled,
      onError = noop,
    },
    outerRef
  ) => {
    const onErrorRef = useUpdatingRef(onError)
    const [, updateComponentVersion] = useState(0)
    const innerRef = useRef()
    const ref = outerRef || innerRef

    const updateContent = useCallback(() => {
      ref.current.value = ref.current.innerText
      try {
        ref.current.innerHTML = addStyles(ref.current.value, disabled)
        ref.current.error = null
      } catch (err) {
        ref.current.innerHTML = ref.current.value
        ref.current.error = err
      }
      updateComponentVersion(v => v + 1)
    }, [ref, disabled])

    const error = ref?.current?.error

    useEffect(() => {
      onErrorRef.current(error)
    }, [onErrorRef, ref, error])

    useEffect(() => {
      if (ref?.current?.value !== value) {
        ref.current.innerHTML = isNullOrUndefined(value) ? '' : value
        ref.current.value = value
      }
      updateContent()
    }, [ref, value, updateContent])

    return (
      <div
        ref={ref}
        style={{ whiteSpace: 'nowrap' }}
        className="tf_area"
        contentEditable
        onKeyDown={event => {
          if (event.key === 'Enter' || disabled) event.preventDefault()
        }}
        onPaste={event => {
          event.preventDefault()
          const parsedText = getClipboardData(event)
          pasteAsText(parsedText)
          updateContent()
          onChange({ target: { value: parsedText } })
        }}
        onBlur={e => {
          if (e.target.value !== e.target.innerText) {
            updateContent()
            onChange(e)
          }
          onBlur(e)
        }}
        onFocus={e => {
          ref.current.value = ref.current.innerText
          onFocus(e)
        }}
      />
    )
  }
)

URLInput.propTypes = InputProptypes
URLInput.displayName = 'URLInput'

export default wrap(URLInput, { className: 'box_textarea' })
